import { useMemo } from 'react'
import { IntegrationServiceId } from '../../../../../../common/integrations'
import { SelectDropdown, SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../store/hooks'
import { useGetDescribeQuery, useGetSupportedObjectsQuery } from '../../../../../../store/integrations/integrations'
import { FormFieldDefinition, useGetIntegrationAdminSettings } from '../../hooks'

export interface ObjectAndFieldSelectDropdownProps {
  serviceId: IntegrationServiceId
  objectName: string | null
  initialSelectedField: string | null
  onObjectSelected: (option: SelectOption) => void
  onFieldSelected: (option: SelectOption, fieldDefinition?: FormFieldDefinition) => void
  onFieldCleared?: () => void
  predefinedObjectSelectOptions?: SelectOption[]
  predefinedFieldSelectOptions?: SelectOption[]
  isObjectSelectionDisabled?: boolean
  isFieldSelectionDisabled?: boolean
}

export const ObjectAndFieldSelectDropdown: React.FC<ObjectAndFieldSelectDropdownProps> = ({
  serviceId,
  objectName,
  initialSelectedField,
  onObjectSelected,
  onFieldSelected,
  onFieldCleared,
  predefinedObjectSelectOptions = null,
  predefinedFieldSelectOptions = null,
  isObjectSelectionDisabled = false,
  isFieldSelectionDisabled = false,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: objectName! },
    },
    { skip: !objectName || !accountId || predefinedFieldSelectOptions !== null },
  )
  const supportedObjectsSelectOption = useMemo(() => {
    if (predefinedObjectSelectOptions) {
      return predefinedObjectSelectOptions
    }

    if (isLoadingSupportedObjects || !supportedObjects) {
      return []
    }
    return supportedObjects.map((obj) => ({
      id: obj.name,
      label: `${obj.label} (${obj.name})`,
    }))
  }, [supportedObjects, isLoadingSupportedObjects, predefinedObjectSelectOptions])

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const fieldSelectOptions = useMemo(() => {
    if (predefinedFieldSelectOptions) {
      return predefinedFieldSelectOptions
    }

    if (isDescribeFetching) {
      return []
    }

    return currentObjectFormFieldDefinitions.map((field) => ({
      id: field.name,
      label: `${field.label} (${field.name})`,
    }))
  }, [currentObjectFormFieldDefinitions, isDescribeFetching, predefinedFieldSelectOptions])

  const initialSelectedObjectOption: SelectOption | null = useMemo(() => {
    if (!objectName) {
      return null
    }
    if (predefinedObjectSelectOptions) {
      return predefinedObjectSelectOptions.find((opt) => opt.id === objectName) ?? null
    }
    const obj = supportedObjects?.find((obj) => obj.name === objectName)
    if (!obj) {
      return null
    }

    return {
      id: obj.name,
      label: `${obj.label} (${obj.name})`,
    }
  }, [objectName, supportedObjects, predefinedObjectSelectOptions])

  const initialSelectedFieldOption: SelectOption | null = useMemo(() => {
    if (!objectName || !initialSelectedField) {
      return null
    }
    if (predefinedFieldSelectOptions) {
      return predefinedFieldSelectOptions.find((opt) => opt.id === initialSelectedField) ?? null
    }
    const field = currentObjectFormFieldDefinitions.find((field) => field.name === initialSelectedField)
    if (!field) {
      return null
    }

    return {
      id: field.name,
      label: `${field.label} (${field.name})`,
    }
  }, [objectName, initialSelectedField, currentObjectFormFieldDefinitions, predefinedFieldSelectOptions])

  const onFieldOptionSelected = (option: SelectOption) =>
    onFieldSelected(
      option,
      currentObjectFormFieldDefinitions.find((field) => field.name === option.id),
    )

  return (
    <>
      <div className='flex-grow-1'>
        <SelectDropdown
          searchable
          options={supportedObjectsSelectOption}
          initialSelectedOption={initialSelectedObjectOption}
          placeholder='Select object'
          onOptionSelected={onObjectSelected}
          loading={isLoadingSupportedObjects}
          disabled={isObjectSelectionDisabled}
          className='w-100'
        />
      </div>

      <div className='ms-2 flex-grow-1'>
        <SelectDropdown
          searchable
          options={fieldSelectOptions}
          initialSelectedOption={initialSelectedFieldOption}
          placeholder='Select field'
          onOptionSelected={onFieldOptionSelected}
          onClear={onFieldCleared}
          loading={isDescribeFetching}
          disabled={isFieldSelectionDisabled}
          className='w-100'
        />
      </div>
    </>
  )
}
